import React, { useContext, useState } from "react";
import { Table as SemanticTable } from "semantic-ui-react";
import { DateTimeUtils, DYLAvatar, OnHoverOnClick, Table, TableLoader } from "dyl-components";

import { QuoteBuilderContext } from "shared/context/QuoteBuilderProvider";
import { StringUtils } from "utils";
import QuoteID from "shared/QuoteID";
import HoverContent from "shared/hoverContent";
import PaymentInfo from "shared/PaymentInfo";
import { useDispatch, useSelector } from "react-redux";
import InvoiceActions from "./InvoiceActions";
import invoiceActions from "actions/invoice";

const InvoiceRow = ({ invoice, account_id, onModify, isModifying }) => {
    const isDyl = useSelector(state => state.auth.dyl_employee)
    const { onViewInvoice } = useContext(QuoteBuilderContext);
    return (
        <>
            <SemanticTable.Cell>
                <div>
                    <b>
                        {DateTimeUtils.formatEpoch(
                            invoice.created,
                            DateTimeUtils.WORD_DATE_FORMAT
                        )}
                    </b>
                </div>
                <div>
                    <b>
                        {DateTimeUtils.formatEpoch(
                            invoice.created,
                            DateTimeUtils.TIME_FORMAT
                        )}
                    </b>
                </div>
            </SemanticTable.Cell>
            <SemanticTable.Cell>
                {invoice.due_on > 0 && (
                    <>
                        <div>
                            <b>
                                {DateTimeUtils.formatEpoch(
                                    invoice.due_on,
                                    DateTimeUtils.WORD_DATE_FORMAT
                                )}
                            </b>
                        </div>
                        <div>
                            <b>
                                {DateTimeUtils.formatEpoch(
                                    invoice.due_on,
                                    DateTimeUtils.TIME_FORMAT
                                )}
                            </b>
                        </div>
                    </>
                )}
            </SemanticTable.Cell>
            <SemanticTable.Cell>
                <QuoteID
                    hasPdf
                    id={invoice.id}
                    onEditQuote={() => {
                        onViewInvoice(invoice.id);
                    }}
                    onViewProposal={() => {
                        onViewInvoice(invoice.id);
                    }}
                />
            </SemanticTable.Cell>
            <SemanticTable.Cell>
                {invoice.amount &&
                    `$${StringUtils.formatDecimal(
                        `${invoice.amount}`,
                        true
                    )}`}
            </SemanticTable.Cell>
            {!isDyl && <SemanticTable.Cell>
                {StringUtils.capitalize(invoice.billed)}
            </SemanticTable.Cell>}
            <SemanticTable.Cell>
                {!invoice.payment_type && invoice.payment_data ? <PaymentInfo info={invoice.payment_data} /> : StringUtils.toSentenceCase(invoice.payment_type?.split("_")?.join(" ") || "None")}
            </SemanticTable.Cell>
            <SemanticTable.Cell>
                {StringUtils.capitalize(invoice.invoice_status)}
            </SemanticTable.Cell>
            {!isDyl && (
                <SemanticTable.Cell>
                    <InvoiceActions account_id={account_id} invoice={invoice} onModify={onModify} isModifying={isModifying} />
                </SemanticTable.Cell>
            )}
        </>
    );
};

const Subrows = ({ invoiceLogs }) => {
    return (
        <Table.CollapsibleRowContent className="InvoiceRow__Subrows">
            <Table.Cell colSpan={5}>
                <SemanticTable striped={false} >
                    <SemanticTable.Header>
                        <SemanticTable.Row>
                            <SemanticTable.HeaderCell>
                                Date
                            </SemanticTable.HeaderCell>
                            <SemanticTable.HeaderCell>
                                Activity Type
                            </SemanticTable.HeaderCell>
                            <SemanticTable.HeaderCell>
                                Description
                            </SemanticTable.HeaderCell>
                            <SemanticTable.HeaderCell width={1}>
                                User
                            </SemanticTable.HeaderCell>
                        </SemanticTable.Row>
                    </SemanticTable.Header>
                    <Table.Body>
                        {invoiceLogs.map((log) => {
                            const description = log.description;
                            const change = (description.replace(/set to/i, "").replace(/changed to/i, "").trim());
                            return (
                                <SemanticTable.Row key={log.id}>
                                    <SemanticTable.Cell>
                                        <div><b>{log.date && DateTimeUtils.formatEpoch(log.date, DateTimeUtils.WORD_DATE_FORMAT)}</b></div>
                                        <div>{log.date && DateTimeUtils.formatEpoch(log.date, DateTimeUtils.TIME_FORMAT)}</div>
                                    </SemanticTable.Cell>
                                    <SemanticTable.Cell>
                                        {StringUtils.toSentenceCase(log.activity_type.split("_").join(" "))}
                                    </SemanticTable.Cell>
                                    <SemanticTable.Cell>
                                        {change !== '' ? description.replaceAll(change, StringUtils.toSentenceCase(change.split("_").join(" "))) : `${description} None`}
                                    </SemanticTable.Cell>
                                    <SemanticTable.Cell>
                                        <OnHoverOnClick
                                            hoverContent={<HoverContent
                                                user={log.user_info} />}
                                            clickable={false}
                                            trigger={(
                                                <DYLAvatar
                                                    name={log.user_info.first_name + " " + log.user_info.last_name}
                                                    inactive={log.user_info ? !log.user_info.active : false}
                                                    size='2.5em'
                                                    disabled
                                                    title={log.user_info.username} />
                                            )} />

                                    </SemanticTable.Cell>
                                </SemanticTable.Row>
                            );
                        })}
                    </Table.Body>
                </SemanticTable>
            </Table.Cell>
            <Table.Cell colSpan={4}/>
        </Table.CollapsibleRowContent>
    )
}

const InvoiceRowWrapper = ({ isCollapsible, invoice, account_id, onModify, isModifying }) => {
    const [collapsed, setCollapsed] = useState(true);
    const dispatch = useDispatch();
    const { invoiceBeingReadForLogs, invoiceLogs } = useSelector((state) => state.invoice)
    const onToggle = () => {
        if(collapsed){
            dispatch(invoiceActions.getInvoiceChangeLog(invoice.id, { account_id }))
        }
        setCollapsed(!collapsed);
    };
    if (isCollapsible) {
        return (
            <Table.CollapsibleRow
                onToggle={onToggle}
                collapsed={collapsed}
                toggleDisabled={!invoice.has_logs}
                subrowContent={
                    (invoiceBeingReadForLogs === invoice.id) ? (
                        <TableLoader colspan={9} isLoading />
                    ) : (
                        <Subrows invoiceLogs={invoiceLogs[invoice.id] || []} />
                    )
                }
            >
                <InvoiceRow invoice={invoice} account_id={account_id} onModify={onModify} isModifying={isModifying} />
            </Table.CollapsibleRow>
        );
    }
    return (
        <Table.Row>
            <InvoiceRow invoice={invoice} account_id={account_id} onModify={onModify} isModifying={isModifying} />
        </Table.Row>
    );
}

export default InvoiceRowWrapper;
